import {Button, Modal, Tag} from "antd";
import {useEffect, useState} from "react";
import OTPInput from "react-otp-input";
import styled from "styled-components";
import {useGetProfileContext} from "../../../context/profile/profile-context";
import {
  useInitiateTotp,
  useRequestEmailTwoFactorSetup,
  useActivateEmailTwoFactor,
} from "../../../react-query-hooks/two-factor";
import {
  StyledOtpInput,
  StyledTextButton,
} from "../../payouts/v2/tabs/payout-settings-tab/otp-view/styles";

const StyledSetupOtpInput = styled(StyledOtpInput)`
  && {
    flex: 1 1 0;
    width: 100% !important;
    min-width: 0;
    height: 6.4rem !important;
  }
`;

const StyledMethodOption = styled.div<{active?: boolean}>`
  padding: 16px;
  border-radius: 6px;
  cursor: pointer;
  border: 1px solid ${({active}) => (active ? "#2E2640" : "#F0F0F0")};
  background-color: ${({active}) => (active ? "#EAE6F3" : "#fff")};

  &:hover {
    background-color: ${({active}) => (active ? "#EAE6F3" : "#F5F5F5")};
  }
`;

type TTwoFactorSetupStep = "select" | "email-otp";
type TTwoFactorSetupMethod = "totp" | "email" | null;

interface TwoFactorSetupModalProps {
  visible: boolean;
  onClose: () => void;
  onTotpInitiated: (totpData: {qrUri: string; secret: string}) => void;
  onEmailSetupSuccess: () => void;
}

export function TwoFactorSetupModal({
  visible,
  onClose,
  onTotpInitiated,
  onEmailSetupSuccess,
}: TwoFactorSetupModalProps) {
  const {state: profileState} = useGetProfileContext();
  const userEmail = profileState.profileData.email;

  const [step, setStep] = useState<TTwoFactorSetupStep>("select");
  const [selectedMethod, setSelectedMethod] = useState<TTwoFactorSetupMethod>(null);
  const [otp, setOtp] = useState("");
  const [timer, setTimer] = useState(30);

  const initiateTotpMutation = useInitiateTotp();
  const requestEmailSetupMutation = useRequestEmailTwoFactorSetup();
  const activateEmailMutation = useActivateEmailTwoFactor();

  useEffect(() => {
    if (visible) {
      setStep("select");
      setSelectedMethod(null);
      setOtp("");
      setTimer(30);
    }
  }, [visible]);

  useEffect(() => {
    if (step !== "email-otp") {
      return;
    }

    const timerCount = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerCount);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerCount);
  }, [step]);

  const stepCount = selectedMethod === "email" ? 2 : 4;

  const handleNext = () => {
    if (selectedMethod === "totp") {
      initiateTotpMutation.mutate(undefined, {
        onSuccess: (response: any) => {
          onTotpInitiated(response.data);
        },
      });
    } else if (selectedMethod === "email") {
      requestEmailSetupMutation.mutate(undefined, {
        onSuccess: () => setStep("email-otp"),
      });
    }
  };

  const handleResendOtp = () => {
    requestEmailSetupMutation.mutate(undefined, {
      onSuccess: () => {
        setTimer(30);
        setOtp("");
      },
    });
  };

  const handleVerifyEmailOtp = () => {
    activateEmailMutation.mutate(
      {twofaCode: otp},
      {
        onSuccess: () => onEmailSetupSuccess(),
        onError: () => setOtp(""),
      }
    );
  };

  const isLoading = initiateTotpMutation.isLoading || requestEmailSetupMutation.isLoading;

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      closable
      centered
      width={520}
      bodyStyle={{padding: 16}}
    >
      {step === "select" && (
        <div style={{display: "flex", flexDirection: "column", gap: "24px"}}>
          <div>
            <p
              style={{
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: 1,
                textTransform: "uppercase",
                color: "rgba(0,0,0,0.45)",
                margin: 0,
              }}
            >
              Step 1 of {stepCount}
            </p>
            <div style={{borderBottom: "1px solid #F0F0F0", margin: "8px -16px 0"}} />
          </div>

          <div style={{display: "flex", flexDirection: "column", gap: "8px"}}>
            <h2 style={{fontSize: "2rem", fontWeight: 600, margin: 0}}>
              Select an option
            </h2>
            <p
              style={{
                fontFamily: "Inter",
                fontWeight: 400,
                fontSize: 14,
                lineHeight: "22px",
                letterSpacing: 0,
                color: "#1C1726",
                margin: 0,
              }}
            >
              Choose how you want to verify it&apos;s you
            </p>
          </div>

          <div style={{display: "flex", flexDirection: "column", gap: "12px"}}>
            <StyledMethodOption
              active={selectedMethod === "totp"}
              onClick={() => setSelectedMethod("totp")}
            >
              <div style={{display: "flex", alignItems: "center", gap: 8}}>
                <span
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    border: "1px solid #8C8C8C",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {selectedMethod === "totp" && (
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: "#2E2640",
                      }}
                    />
                  )}
                </span>
                <strong>Authenticator app</strong>
                <Tag
                  style={{
                    width: 112,
                    height: 22,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: 0,
                    borderRadius: 4,
                    padding: "1px 8px",
                    border: "1px solid #D3ADF7",
                    background: "#F9F0FF",
                    color: "#722ED1",
                    fontFamily: "Inter",
                    fontWeight: 600,
                    fontSize: 12,
                    lineHeight: "20px",
                    letterSpacing: 0,
                  }}
                >
                  RECOMMENDED
                </Tag>
              </div>
              <p
                style={{
                  margin: "4px 0 0 24px",
                  fontFamily: "Inter",
                  fontWeight: 400,
                  fontSize: 14,
                  lineHeight: "22px",
                  letterSpacing: 0,
                  color: "#595959",
                }}
              >
                Generate codes with any authenticator app, like Google, Authy, etc.
              </p>
            </StyledMethodOption>

            <StyledMethodOption
              active={selectedMethod === "email"}
              onClick={() => setSelectedMethod("email")}
            >
              <div style={{display: "flex", alignItems: "center", gap: 8}}>
                <span
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    border: "1px solid #8C8C8C",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {selectedMethod === "email" && (
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: "#2E2640",
                      }}
                    />
                  )}
                </span>
                <strong>Email verification</strong>
              </div>
              <p
                style={{
                  margin: "4px 0 0 24px",
                  fontFamily: "Inter",
                  fontWeight: 400,
                  fontSize: 14,
                  lineHeight: "22px",
                  letterSpacing: 0,
                  color: "#595959",
                }}
              >
                Get a one-time code sent to {userEmail}
              </p>
            </StyledMethodOption>
          </div>

          <div style={{display: "flex", gap: "12px", width: "100%"}}>
            <Button size="small" style={{flex: 1}} onClick={onClose}>
              Cancel
            </Button>
            <Button
              size="small"
              style={{flex: 1}}
              type="primary"
              disabled={!selectedMethod}
              loading={isLoading}
              onClick={handleNext}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {step === "email-otp" && (
        <div style={{display: "flex", flexDirection: "column", gap: "24px"}}>
          <div>
            <p
              style={{
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: 1,
                textTransform: "uppercase",
                color: "rgba(0,0,0,0.45)",
                margin: 0,
              }}
            >
              Step 2 of 2
            </p>
            <div style={{borderBottom: "1px solid #F0F0F0", margin: "8px -16px 0"}} />
          </div>

          <div style={{display: "flex", flexDirection: "column", gap: "24px"}}>
            <div style={{display: "flex", flexDirection: "column", gap: "8px"}}>
              <h2 style={{fontSize: "2rem", fontWeight: 600, margin: 0}}>
                Check your email
              </h2>
              <p
                style={{
                  fontFamily: "Inter",
                  fontWeight: 400,
                  fontSize: 14,
                  lineHeight: "22px",
                  letterSpacing: 0,
                  color: "#1C1726",
                  margin: 0,
                }}
              >
                We sent a 6-digit code to {userEmail}. Enter it below to continue
              </p>
            </div>
            <OTPInput
              value={otp}
              onChange={setOtp}
              numInputs={6}
              shouldAutoFocus
              inputType="tel"
              containerStyle={{display: "flex", gap: "12px", width: "100%"}}
              renderInput={(props) => <StyledSetupOtpInput {...props} />}
            />
          </div>

          <p style={{margin: 0}}>
            Didn&apos;t get code?{" "}
            {timer > 0 ? (
              <span style={{color: "rgba(0,0,0,0.45)"}}>Resend in {timer}s</span>
            ) : (
              <StyledTextButton
                type="text"
                size="small"
                loading={requestEmailSetupMutation.isLoading}
                onClick={handleResendOtp}
              >
                <strong style={{fontWeight: 600}}>Resend code</strong>
              </StyledTextButton>
            )}
          </p>
          <div style={{display: "flex", gap: "12px", width: "100%"}}>
            <Button size="small" style={{flex: 1}} onClick={() => setStep("select")}>
              Back
            </Button>
            <Button
              size="small"
              style={{flex: 1}}
              type="primary"
              disabled={otp.length < 6}
              loading={activateEmailMutation.isLoading}
              onClick={handleVerifyEmailOtp}
            >
              Verify code
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
